/**
 * PlacementSystem.ts
 * Manages player-placed buildings: placement mode, tile validation,
 * construction progress, and income/effect ticking.
 */
import type { TileMap } from '../map/TileMap';
import type { ResourceSystem } from './ResourceSystem';
import type { Unit } from '../entities/Unit';
import { Faction, TerrainType, type BuildingType, type PlacedBuilding } from '../types';
import {
  BUILD_PUB_COST_REP, BUILD_PUB_COST_SALES,
  BUILD_FLYER_COST_REP, BUILD_FLYER_COST_SALES,
  BUILD_MERCH_COST_REP, BUILD_MERCH_COST_SALES,
  BUILD_RIG_COST_REP, BUILD_RIG_COST_GEAR,
  BUILD_PUB_REP_INCOME, BUILD_MERCH_SALES_INCOME,
  BUILD_FLYER_FAN_BONUS,
  BUILD_TIME_PUB, BUILD_TIME_FLYER, BUILD_TIME_MERCH, BUILD_TIME_RIG,
  BUILD_RIG_ATTACK_RADIUS, BUILD_RIG_DAMAGE, BUILD_RIG_ATTACK_RATE,
} from '../constants';

// Definition for each buildable type
export interface BuildingDef {
  type:        BuildingType;
  name:        string;
  description: string;
  costRep:     number;
  costSales:   number;
  costGear:    number;
  buildTime:   number;   // seconds
  maxHp:       number;
  colour:      number;   // hex for renderer
}

export const BUILDING_DEFS: BuildingDef[] = [
  {
    type: 'Pub', name: 'Pub',
    description: `+${BUILD_PUB_REP_INCOME} REP/s`,
    costRep: BUILD_PUB_COST_REP, costSales: BUILD_PUB_COST_SALES, costGear: 0,
    buildTime: BUILD_TIME_PUB, maxHp: 120, colour: 0xffaa22,
  },
  {
    type: 'FlyerPost', name: 'Flyer Post',
    description: '+10 FAN cap',
    costRep: BUILD_FLYER_COST_REP, costSales: BUILD_FLYER_COST_SALES, costGear: 0,
    buildTime: BUILD_TIME_FLYER, maxHp: 60, colour: 0x22ddcc,
  },
  {
    type: 'MerchStall', name: 'Merch Stall',
    description: '+1.5 SALES/s',
    costRep: BUILD_MERCH_COST_REP, costSales: BUILD_MERCH_COST_SALES, costGear: 0,
    buildTime: BUILD_TIME_MERCH, maxHp: 80, colour: 0x44cc66,
  },
  {
    type: 'SoundRig', name: 'Sound Rig',
    description: 'Attacks nearby enemies',
    costRep: BUILD_RIG_COST_REP, costSales: 0, costGear: BUILD_RIG_COST_GEAR,
    buildTime: BUILD_TIME_RIG, maxHp: 150, colour: 0x8866ff,
  },
];

function makeid(): string {
  return `b_${Math.random().toString(36).slice(2, 9)}`;
}

export class PlacementSystem {
  readonly buildings: PlacedBuilding[] = [];

  /** Which building type is currently being placed (null = not in placement mode). */
  placementType: BuildingType | null = null;
  /** The tile currently under the cursor during placement mode. */
  ghostTile: { col: number; row: number } | null = null;

  /** Set of occupied tile keys 'col,row' for fast validity checks. */
  private occupiedTiles = new Set<string>();

  constructor(
    private readonly resources: ResourceSystem,
    private readonly map:       TileMap,
  ) {}

  // ── Placement mode ────────────────────────────────────────────────────

  enterPlacementMode(type: BuildingType): void {
    this.placementType = type;
    this.ghostTile = null;
  }

  cancelPlacement(): void {
    this.placementType = null;
    this.ghostTile = null;
  }

  updateGhostTile(col: number | null, row: number | null): void {
    if (col === null || row === null) { this.ghostTile = null; return; }
    this.ghostTile = { col, row };
  }

  canPlace(col: number, row: number, venueOccupied: (c: number, r: number) => boolean): boolean {
    if (!this.map.inBounds(col, row)) return false;
    if (this.map.isBeacon(col, row)) return false;
    const t = this.map.getTerrain({ col, row });
    if (t !== TerrainType.Grass && t !== TerrainType.Path) return false;
    if (this.occupiedTiles.has(`${col},${row}`)) return false;
    if (venueOccupied(col, row)) return false;
    return true;
  }

  tryPlace(
    col: number, row: number,
    venueOccupied: (c: number, r: number) => boolean,
  ): boolean {
    if (!this.placementType) return false;
    if (!this.canPlace(col, row, venueOccupied)) return false;

    const def = BUILDING_DEFS.find(d => d.type === this.placementType)!;

    // Deduct cost
    if (this.resources.reputation < def.costRep) return false;
    if (this.resources.sales      < def.costSales) return false;
    if (this.resources.gear       < def.costGear) return false;

    this.resources.add('reputation', -def.costRep);
    this.resources.add('sales',      -def.costSales);
    if (def.costGear > 0) this.resources.add('gear', -def.costGear);

    const b: PlacedBuilding = {
      id: makeid(), type: this.placementType,
      col, row,
      constructionProgress: 0,
      constructed: false,
      hp: def.maxHp, maxHp: def.maxHp,
      attackCooldown: 0,
    };

    this.buildings.push(b);
    this.occupiedTiles.add(`${col},${row}`);
    this.map.setTerrain({ col, row }, TerrainType.Building);

    const placedType = this.placementType;
    this.cancelPlacement();
    // Restore placementType so caller can read it for the toast message
    // (cancelPlacement clears it, so we return the type via the building list)
    void placedType;
    return true;
  }

  // ── Update loop ───────────────────────────────────────────────────────

  update(delta: number, units: Unit[]): void {
    let flyerCount = 0;

    for (const b of this.buildings) {
      if (!b.constructed) {
        const def = BUILDING_DEFS.find(d => d.type === b.type)!;
        b.constructionProgress = Math.min(1, b.constructionProgress + delta / def.buildTime);
        if (b.constructionProgress >= 1) b.constructed = true;
        continue;
      }

      switch (b.type) {
        case 'Pub':
          this.resources.add('reputation', BUILD_PUB_REP_INCOME * delta);
          break;
        case 'MerchStall':
          this.resources.add('sales', BUILD_MERCH_SALES_INCOME * delta);
          break;
        case 'FlyerPost':
          flyerCount++;
          break;
        case 'SoundRig':
          this.tickSoundRig(b, delta, units);
          break;
      }
    }

    // FlyerPost bonus — recalculate every frame (cheap)
    this.resources.extraFanbaseCap = flyerCount * BUILD_FLYER_FAN_BONUS;
  }

  private tickSoundRig(b: PlacedBuilding, delta: number, units: Unit[]): void {
    b.attackCooldown = Math.max(0, b.attackCooldown - delta);
    if (b.attackCooldown > 0) return;

    // Find nearest living enemy in range
    let nearest: Unit | null = null;
    let nearestDist = Infinity;
    for (const u of units) {
      if (!u.alive || u.isDowned || u.faction !== Faction.Enemy) continue;
      const d = Math.hypot(u.gridPos.col - b.col, u.gridPos.row - b.row);
      if (d <= BUILD_RIG_ATTACK_RADIUS && d < nearestDist) {
        nearest = u; nearestDist = d;
      }
    }
    if (!nearest) return;
    nearest.hp -= BUILD_RIG_DAMAGE;
    if (nearest.hp <= 0) nearest.alive = false;
    b.attackCooldown = BUILD_RIG_ATTACK_RATE;
  }

  // ── Save / load ───────────────────────────────────────────────────────

  getState(): PlacedBuilding[] {
    return this.buildings.map(b => ({ ...b }));
  }

  loadState(state: PlacedBuilding[]): void {
    this.buildings.length = 0;
    this.occupiedTiles.clear();
    for (const b of state) {
      this.buildings.push({ ...b });
      this.occupiedTiles.add(`${b.col},${b.row}`);
      if (b.constructed) {
        this.map.setTerrain({ col: b.col, row: b.row }, TerrainType.Building);
      }
    }
  }

  canAfford(type: BuildingType): boolean {
    const def = BUILDING_DEFS.find(d => d.type === type);
    if (!def) return false;
    return this.resources.reputation >= def.costRep
        && this.resources.sales      >= def.costSales
        && this.resources.gear       >= def.costGear;
  }
}
